import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Trash2, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SaveLoadDialog = ({ 
  open, 
  onOpenChange, 
  mode, // 'save' or 'load'
  currentText,
  currentStoneColor,
  currentTextColor,
  onLoad 
}) => {
  const [title, setTitle] = useState('');
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inscriptions`);
      setInscriptions(response.data);
    } catch (error) {
      console.error('Error fetching inscriptions:', error);
      toast.error('Failed to load inscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'load') {
      fetchInscriptions();
    }
  }, [open, mode, fetchInscriptions]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!currentText.trim()) {
      toast.error('Cannot save empty inscription');
      return;
    }

    setLoading(true);
    try {
      const lines = currentText.split('\n').filter(line => line.trim());
      await axios.post(`${API}/inscriptions`, {
        title: title.trim(),
        text: currentText,
        lines,
        stone_color: currentStoneColor,
        text_color: currentTextColor
      });
      toast.success('Inscription saved successfully!');
      setTitle('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving inscription:', error);
      toast.error('Failed to save inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadInscription = (inscription) => {
    onLoad(inscription);
    toast.success(`Loaded: ${inscription.title}`);
    onOpenChange(false);
  };

  const handleDelete = async (inscriptionId, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;

    try {
      await axios.delete(`${API}/inscriptions/${inscriptionId}`);
      toast.success('Inscription deleted');
      fetchInscriptions();
    } catch (error) {
      console.error('Error deleting inscription:', error);
      toast.error('Failed to delete inscription');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="save-load-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">
            {mode === 'save' ? 'Save Inscription' : 'Load Inscription'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'save' 
              ? 'Enter a title to save your inscription to the database'
              : 'Select an inscription to load from the database'
            }
          </DialogDescription>
        </DialogHeader>

        {mode === 'save' ? (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Deborah Adele Saka Memorial"
                className="focus:ring-gold-500"
                data-testid="save-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="p-4 bg-stone-50 rounded border border-stone-200 font-hebrew text-right max-h-[200px] overflow-y-auto" dir="rtl">
                {currentText || <span className="text-stone-400 font-manrope">No text to save</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-4">
            {loading ? (
              <div className="text-center py-8 text-stone-500">Loading inscriptions...</div>
            ) : inscriptions.length === 0 ? (
              <div className="text-center py-8 text-stone-500">No saved inscriptions found</div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {inscriptions.map((inscription) => (
                    <div
                      key={inscription.id}
                      className="p-4 bg-stone-50 rounded border border-stone-200 hover:bg-stone-100 transition-colors cursor-pointer group"
                      onClick={() => handleLoadInscription(inscription)}
                      data-testid={`inscription-item-${inscription.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-stone-900 mb-1">{inscription.title}</h4>
                          <p className="text-sm text-stone-600 font-hebrew line-clamp-2" dir="rtl">
                            {inscription.text}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                            <Calendar className="h-3 w-3" />
                            {formatDate(inscription.created_at)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(inscription.id, inscription.title);
                          }}
                          data-testid={`delete-button-${inscription.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        {mode === 'save' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-button">
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={loading || !title.trim() || !currentText.trim()}
              className="bg-slate-700 hover:bg-slate-800"
              data-testid="confirm-save-button"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaveLoadDialog;